import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, SelectChangeEvent, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

import CustomBackdrop from '../../../components/CustomBackdrop/CustomBackdrop';
import {
  useCortexFeedbackPipedriveBridgeMutation,
  useCreatePdOrganizationMutation,
  useGetBuyAndBuildPlatformsQuery,
  usePrecalculateAllKpisMutation,
} from '../../../gql/generated/graphql';
import { paths } from '../../../routes/paths';
import BIDPlatform from '../../ValidationPage/components/BIDPlatform/BIDPlatform';
import { TrackASteps } from '../types';

const stepToReasonMap: Record<number, string | null> = {
  [TrackASteps.IS_SOFTWARE]: null,
  [TrackASteps.PRODUCTS_BUSINESS_MODEL]: 'wrongBusinessModel',
  [TrackASteps.CUSTOMER_REFERENCES]: 'b2c',
  [TrackASteps.ADDRESS]: 'wrongGeography',
  [TrackASteps.SHAREHOLDERS]: 'otherOwnership',
  [TrackASteps.CERTIFICATIONS]: null,
  [TrackASteps.TECHNOLOGY]: 'wrongBusinessModel',
  [TrackASteps.COMPANY_FINISH]: null,
};

interface Props {
  companyId: number;
  activeStep: number;
  onReleaseLock?: () => Promise<void>;
}

const NotRelevantRejectionV2 = ({ companyId, activeStep, onReleaseLock }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthenticator();

  const [isExpanded, setIsExpanded] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);

  const { data: platformsData, loading: platformsLoading } = useGetBuyAndBuildPlatformsQuery({
    variables: { sortForTargetId: companyId, excludeAddOns: true },
    skip: !isExpanded,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  const platforms = platformsData?.getBuyAndBuildPlatforms?.edges?.map(edge => edge?.node);

  const [createPDOrganization, { loading: createPDLoading }] = useCreatePdOrganizationMutation();
  const [precalculateAllKpis, { loading: kpisLoading }] = usePrecalculateAllKpisMutation();
  const [addBidPlatform, { loading: bidLoading }] = useCortexFeedbackPipedriveBridgeMutation();

  const reason = stepToReasonMap[activeStep];
  if (!reason) return null;

  const handleSubmit = async () => {
    try {
      const authId = user.username.replace('GoogleWorkspace_', '');
      const formData = {
        newPlatform: false,
        addOnExisting: false,
        addOnFuture: false,
        saas: false,
        venture: false,
        none: false,
        partOfGroup: false,
        otherOwnership: reason === 'otherOwnership',
        sellsHardware: false,
        tooMuchConsulting: false,
        reseller: false,
        wrongBusinessModel: reason === 'wrongBusinessModel',
        b2c: reason === 'b2c',
        wrongGeography: reason === 'wrongGeography',
        tooBig: false,
        tooSmall: reason === 'tooSmall',
        createOrganization: false,
        createDeal: false,
        selectValue: '',
        note: '',
      };

      await createPDOrganization({ variables: { companyId, authId, ...formData } });
      precalculateAllKpis({ variables: { companyId } });

      if (platform) {
        addBidPlatform({ variables: { addOnForBidId: parseInt(platform), companyId } });
      }

      if (onReleaseLock) await onReleaseLock();
      navigate(paths.crawlerInbox);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const isLoading = createPDLoading || kpisLoading || bidLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 4, pt: 2 }}>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'text.secondary',
          '&:hover': { color: 'error.main' },
        }}
      >
        <CloseIcon sx={{ fontSize: 18, mr: 0.5 }} />
        <Typography variant="body2">{t('validation.notRelevant.label')}</Typography>
      </Box>

      {isExpanded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, width: '100%', maxWidth: 400 }}>
          <Box sx={{ flex: 1 }}>
            {platformsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {t('validation.notRelevant.loading')}
                </Typography>
              </Box>
            ) : (
              <BIDPlatform
                platform={platform}
                platforms={platforms}
                onHandleChangePlatform={(e: SelectChangeEvent) => setPlatform(e.target.value)}
              />
            )}
          </Box>
          <Button variant="text" color="error" onClick={handleSubmit} disabled={isLoading} sx={{ minWidth: 100 }}>
            {t('validation.notRelevant.submit')}
          </Button>
        </Box>
      )}

      <CustomBackdrop open={isLoading} />
    </Box>
  );
};

export default NotRelevantRejectionV2;
